import { isRecord, MAX_PARTICIPANTS } from "./http";
import { slotStarts, zonedParts } from "./slots";
import type { EventDef, Level, Participant, PollDef, PollVote, SavePatch } from "./types";

export type Result<T> = { ok: true; value: T } | { ok: false; error: string };

const TITLE_MAX = 80;
const NOTE_MAX = 500;
const NAME_MAX = 40;
const PARTICIPANT_NOTE_MAX = 120;
const QUESTION_MAX = 80;
const LABEL_MAX = 40;
const DATES_MAX = 31;
const POLLS_MAX = 5;
const OPTIONS_MIN = 2;
const OPTIONS_MAX = 12;
const SLOTS_MAX = 1488;
const DURATION_MIN = 30;
const DURATION_MAX = 480;
const ID_RE = /^[A-Za-z0-9-_]{1,16}$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const PID_RE = /^[A-Za-z0-9_-]{10}$/;
const TOKEN_RE = /^[A-Za-z0-9_-]{24,64}$/;
const SLOT_KEY_RE = /^[0-9]{1,12}$/;
const ORIGIN_RE = /^[A-Za-z0-9_-]{1,16}$/;

export function validatePid(pid: string): boolean {
  return PID_RE.test(pid);
}

export function validateToken(token: string): boolean {
  return TOKEN_RE.test(token);
}

export function validateCreate(body: unknown, now: string): Result<Omit<EventDef, "id" | "createdAt">> {
  if (!isRecord(body)) return fail("invalid body");
  const title = trimStr(body.title);
  if (!title.ok || !hasVisible(title.value)) return fail("title is required");
  if (title.value.length < 1 || title.value.length > TITLE_MAX) {
    return fail("title must be 1 to 80 characters");
  }
  const note = optionalStr(body.note, NOTE_MAX);
  if (!note.ok) return fail(note.error);
  const timezone = trimStr(body.timezone);
  if (!timezone.ok) return fail("timezone is required");
  if (!isValidTimeZone(timezone.value)) return fail("invalid timezone");
  const dates = parseDates(body.dates, { tz: timezone.value, now, allow: new Set() });
  if (!dates.ok) return fail(dates.error);
  const dayStartMin = parseMinute(body.dayStartMin, "dayStartMin");
  if (!dayStartMin.ok) return fail(dayStartMin.error);
  const dayEndMin = parseMinute(body.dayEndMin, "dayEndMin");
  if (!dayEndMin.ok) return fail(dayEndMin.error);
  if (dayEndMin.value <= dayStartMin.value) return fail("dayEndMin must be greater than dayStartMin");
  if (dayEndMin.value > 1440) return fail("dayEndMin must be at most 1440");
  if (dayStartMin.value < 0) return fail("dayStartMin must be at least 0");
  const durationMin = parseMinute(body.durationMin, "durationMin");
  if (!durationMin.ok) return fail(durationMin.error);
  if (durationMin.value < DURATION_MIN || durationMin.value > DURATION_MAX) {
    return fail("durationMin must be 30 to 480");
  }
  if (durationMin.value > dayEndMin.value - dayStartMin.value) {
    return fail("The meeting is longer than the daily window");
  }
  if ("slotMin" in body && body.slotMin !== 30 && body.slotMin !== undefined) {
    return fail("slotMin must be 30");
  }
  const polls = parsePolls(body.polls);
  if (!polls.ok) return fail(polls.error);
  const deadline = parseDeadline(body.deadline);
  if (!deadline.ok) return fail(deadline.error);
  const value: Omit<EventDef, "id" | "createdAt"> = {
    title: title.value,
    note: note.value,
    timezone: timezone.value,
    dates: dates.value,
    dayStartMin: dayStartMin.value,
    dayEndMin: dayEndMin.value,
    slotMin: 30,
    durationMin: durationMin.value,
    polls: polls.value,
    deadline: deadline.value,
  };
  const slots = slotStarts({
    ...value,
    id: "temp",
    createdAt: "1970-01-01T00:00:00.000Z",
  });
  if (slots.length > SLOTS_MAX) return fail("too many slots");
  if (slots.length < 1) return fail("event must have at least one slot");
  return { ok: true, value };
}

export function validateParticipant(
  body: unknown,
  ev: EventDef,
): Result<
  Pick<Participant, "name" | "note" | "availability" | "pollVotes"> & {
    ignored: number;
    /** Raw, validated value: `undefined` when the body left it out, so the caller can tell "not
     * sent" apart from "sent as some value" (a join defaults to 0, an update keeps what is stored). */
    saveSeq: number | undefined;
    baseSeq: number | undefined;
    origin: string | undefined;
  }
> {
  if (!isRecord(body)) return fail("invalid body");
  const name = trimStr(body.name);
  if (!name.ok || !hasVisible(name.value)) return fail("name is required");
  if (name.value.length < 1 || name.value.length > NAME_MAX) {
    return fail("name must be 1 to 40 characters");
  }
  const note = optionalStr(body.note, PARTICIPANT_NOTE_MAX);
  if (!note.ok) return fail(note.error);
  const availability = parseAvailability(body.availability, ev);
  if (!availability.ok) return fail(availability.error);
  const pollVotes = parsePollVotes(body.pollVotes, ev);
  if (!pollVotes.ok) return fail(pollVotes.error);
  const saveSeq = parseSaveSeq(body.saveSeq);
  if (!saveSeq.ok) return fail(saveSeq.error);
  const baseSeq = parseBaseSeq(body.baseSeq);
  if (!baseSeq.ok) return fail(baseSeq.error);
  const origin = parseOrigin(body.origin);
  if (!origin.ok) return fail(origin.error);
  return {
    ok: true,
    value: {
      name: name.value,
      note: note.value,
      availability: availability.value,
      pollVotes: pollVotes.value,
      ignored: availability.ignored + pollVotes.ignored,
      saveSeq: saveSeq.value,
      baseSeq: baseSeq.value,
      origin: origin.value,
    },
  };
}

/** Patch maps need their own availability parser: zero cannot encode a set or a clear. */
export function validateSavePatch(body: unknown, ev: EventDef): Result<{
  patch: SavePatch;
  ignored: number;
  saveSeq: number | undefined;
  baseSeq: number | undefined;
  origin: string | undefined;
}> {
  if (!isRecord(body) || !isRecord(body.patch)) return fail("patch must be an object");
  const patch: SavePatch = {};
  let ignored = 0;
  for (const field of ["name", "note"] as const) {
    const change = body.patch[field];
    if (change === undefined) continue;
    if (!isRecord(change)) return fail(`${field} patch must contain from and to strings`);
    const from = trimStr(change.from);
    const to = trimStr(change.to);
    if (!from.ok || !to.ok) return fail(`${field} patch must contain from and to strings`);
    const max = field === "name" ? NAME_MAX : PARTICIPANT_NOTE_MAX;
    if (from.value.length > max || to.value.length > max) return fail(`${field} patch is too long`);
    if (field === "name" && (!hasVisible(from.value) || !hasVisible(to.value))) {
      return fail("name is required");
    }
    patch[field] = { from: from.value, to: to.value };
  }
  for (const side of ["set", "clear"] as const) {
    const changes = body.patch[side];
    if (changes === undefined) continue;
    if (!isRecord(changes)) return fail(`${side} patch must be an object`);
    const availability = parsePatchAvailability(changes.availability, ev);
    if (!availability.ok) return fail(availability.error);
    if (changes.pollVotes === null) return fail("pollVotes must be an object");
    const pollVotes = parsePollVotes(changes.pollVotes, ev);
    if (!pollVotes.ok) return fail(pollVotes.error);
    patch[side] = { availability: availability.value, pollVotes: pollVotes.value };
    ignored += availability.ignored + pollVotes.ignored;
  }
  const saveSeq = parseSaveSeq(body.saveSeq);
  if (!saveSeq.ok) return fail(saveSeq.error);
  const baseSeq = parseBaseSeq(body.baseSeq);
  if (!baseSeq.ok) return fail(baseSeq.error);
  const origin = parseOrigin(body.origin);
  if (!origin.ok) return fail(origin.error);
  return { ok: true, value: { patch, ignored, saveSeq: saveSeq.value, baseSeq: baseSeq.value, origin: origin.value } };
}

function parsePatchAvailability(
  v: unknown,
  ev: EventDef,
): { ok: true; value: Record<string, Level>; ignored: number } | { ok: false; error: string } {
  if (v === undefined) return { ok: true, value: {}, ignored: 0 };
  if (!isRecord(v)) return fail("availability must be an object");
  const entries = Object.entries(v);
  if (entries.length > SLOTS_MAX) return fail("too many availability keys");
  const valid = new Set(slotStarts(ev).map(String));
  const out: Record<string, Level> = {};
  let ignored = 0;
  for (const [key, level] of entries) {
    if (!SLOT_KEY_RE.test(key)) return fail("availability key is invalid");
    if (level !== 1 && level !== 2 && level !== 3) return fail("patch availability level must be 1, 2, or 3");
    if (!valid.has(key)) ignored += 1;
    else out[key] = level;
  }
  return { ok: true, value: out, ignored };
}

function parseSaveSeq(v: unknown): Result<number | undefined> {
  if (v === undefined || v === null) return { ok: true, value: undefined };
  if (typeof v !== "number" || !Number.isInteger(v) || v < 1 || v > Number.MAX_SAFE_INTEGER) {
    return fail("saveSeq must be a positive integer");
  }
  return { ok: true, value: v };
}

function parseBaseSeq(v: unknown): Result<number | undefined> {
  if (v === undefined || v === null) return { ok: true, value: undefined };
  if (typeof v !== "number" || !Number.isSafeInteger(v) || v < 0) {
    return fail("baseSeq must be a nonnegative integer");
  }
  return { ok: true, value: v };
}

function parseOrigin(v: unknown): Result<string | undefined> {
  if (v === undefined || v === null) return { ok: true, value: undefined };
  if (typeof v !== "string" || !ORIGIN_RE.test(v)) return fail("origin is invalid");
  return { ok: true, value: v };
}

export function validatePatch(
  body: unknown,
  current: EventDef,
  now: string,
): Result<Partial<Omit<EventDef, "id" | "createdAt">>> {
  if (!isRecord(body)) return fail("invalid body");
  const merged: EventDef = { ...current };
  const out: Partial<Omit<EventDef, "id" | "createdAt">> = {};
  if ("title" in body) {
    const title = trimStr(body.title);
    if (!title.ok || !hasVisible(title.value) || title.value.length < 1 || title.value.length > TITLE_MAX) {
      return fail("title must be 1 to 80 characters");
    }
    merged.title = title.value;
    out.title = title.value;
  }
  if ("note" in body) {
    const note = optionalStr(body.note, NOTE_MAX);
    if (!note.ok) return fail(note.error);
    merged.note = note.value;
    out.note = note.value;
  }
  if ("timezone" in body) {
    const timezone = trimStr(body.timezone);
    if (!timezone.ok || !isValidTimeZone(timezone.value)) return fail("invalid timezone");
    merged.timezone = timezone.value;
    out.timezone = timezone.value;
  }
  if ("dates" in body) {
    const dates = parseDates(body.dates, {
      tz: merged.timezone,
      now,
      allow: new Set(current.dates),
    });
    if (!dates.ok) return fail(dates.error);
    merged.dates = dates.value;
    out.dates = dates.value;
  }
  if ("dayStartMin" in body) {
    const v = parseMinute(body.dayStartMin, "dayStartMin");
    if (!v.ok) return fail(v.error);
    if (v.value < 0) return fail("dayStartMin must be at least 0");
    merged.dayStartMin = v.value;
    out.dayStartMin = v.value;
  }
  if ("dayEndMin" in body) {
    const v = parseMinute(body.dayEndMin, "dayEndMin");
    if (!v.ok) return fail(v.error);
    if (v.value > 1440) return fail("dayEndMin must be at most 1440");
    merged.dayEndMin = v.value;
    out.dayEndMin = v.value;
  }
  if (merged.dayEndMin <= merged.dayStartMin) {
    return fail("dayEndMin must be greater than dayStartMin");
  }
  if ("durationMin" in body) {
    const v = parseMinute(body.durationMin, "durationMin");
    if (!v.ok) return fail(v.error);
    if (v.value < DURATION_MIN || v.value > DURATION_MAX) {
      return fail("durationMin must be 30 to 480");
    }
    merged.durationMin = v.value;
    out.durationMin = v.value;
  }
  if (merged.durationMin > merged.dayEndMin - merged.dayStartMin) {
    return fail("The meeting is longer than the daily window");
  }
  if ("polls" in body) {
    const polls = parsePolls(body.polls);
    if (!polls.ok) return fail(polls.error);
    merged.polls = polls.value;
    out.polls = polls.value;
  }
  if ("deadline" in body) {
    const deadline = parseDeadline(body.deadline);
    if (!deadline.ok) return fail(deadline.error);
    merged.deadline = deadline.value;
    out.deadline = deadline.value;
  }
  merged.slotMin = 30;
  const slots = slotStarts(merged);
  if (slots.length > SLOTS_MAX) return fail("too many slots");
  if (slots.length < 1) return fail("event must have at least one slot");
  return { ok: true, value: out };
}

export function parseExpected(body: unknown): Result<string[]> {
  if (!Array.isArray(body)) return fail("expected must be an array");
  if (body.length > MAX_PARTICIPANTS) return fail("too many expected names");
  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of body) {
    if (typeof item !== "string") return fail("expected names must be strings");
    const name = stripWeird(item).trim();
    if (!hasVisible(name) || name.length < 1 || name.length > NAME_MAX) {
      return fail("expected name must be 1 to 40 characters");
    }
    const key = nameSkeleton(name);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(name);
  }
  return { ok: true, value: out };
}

export function pruneParticipants(participants: Participant[], ev: EventDef): Participant[] {
  const validSlots = new Set(slotStarts(ev).map(String));
  const pollMap = new Map(ev.polls.map((p) => [p.id, new Set(p.options.map((o) => o.id))]));
  return participants.map((p) => {
    const availability: Record<string, Level> = {};
    let droppedAny = false;
    for (const [k, v] of Object.entries(p.availability)) {
      if (validSlots.has(k)) availability[k] = v;
      else droppedAny = true;
    }
    const pollVotes: Record<string, Record<string, PollVote>> = {};
    for (const [pollId, votes] of Object.entries(p.pollVotes)) {
      const opts = pollMap.get(pollId);
      if (!opts) {
        droppedAny = true;
        continue;
      }
      const kept: Record<string, PollVote> = {};
      for (const [optId, vote] of Object.entries(votes)) {
        if (opts.has(optId)) kept[optId] = vote;
        else droppedAny = true;
      }
      pollVotes[pollId] = kept;
    }
    if (!droppedAny) return p;
    return { ...p, availability, pollVotes };
  });
}

export function nameSkeleton(s: string): string {
  return s.normalize("NFKC").replace(/\p{Cf}/gu, "").toLowerCase();
}

function fail(error: string): { ok: false; error: string } {
  return { ok: false, error };
}

/** What the server stores for a title, a note or a label. The client uses it to tell its own text
 * coming back normalized (a trailing space trimmed) from a change made somewhere else. */
export function normalizeText(s: string): string {
  return stripWeird(s).trim();
}

function stripWeird(s: string): string {
  let out = "";
  for (const ch of s.normalize("NFC")) {
    const c = ch.codePointAt(0) ?? 0;
    // Every kind of line break, and a tab, separates words. Deleting them glued the words on either
    // side together ("bring\nsnacks" was stored as "bringsnacks").
    if (c === 0x2028 || c === 0x2029 || c === 0x0085 || c === 0x0a || c === 0x0d || c === 0x09) {
      out += " ";
      continue;
    }
    if (c < 32 || c === 127) continue;
    if (c >= 0x80 && c <= 0x9f) continue;
    if (c >= 0x202a && c <= 0x202e) continue;
    if (c >= 0x2066 && c <= 0x2069) continue;
    out += ch;
  }
  return out;
}

function hasVisible(s: string): boolean {
  return /[\p{L}\p{N}\p{P}\p{S}]/u.test(s);
}

function trimStr(v: unknown): Result<string> {
  if (typeof v !== "string") return fail("expected a string");
  return { ok: true, value: stripWeird(v).trim() };
}

function optionalStr(v: unknown, max: number): Result<string> {
  if (v === undefined || v === null) return { ok: true, value: "" };
  if (typeof v !== "string") return fail("expected a string");
  const s = stripWeird(v).trim();
  if (s.length > max) return fail(`text must be at most ${max} characters`);
  return { ok: true, value: s };
}

function parseMinute(v: unknown, field: string): Result<number> {
  if (typeof v !== "number" || !Number.isInteger(v)) return fail(`${field} must be an integer`);
  if (v % 30 !== 0) return fail(`${field} must be a multiple of 30`);
  return { ok: true, value: v };
}

function addDays(isoDate: string, days: number): string {
  const bits = isoDate.split("-");
  const y = Number(bits[0]);
  const m = Number(bits[1]);
  const d = Number(bits[2]);
  const dt = new Date(Date.UTC(y, m - 1, d + days));
  const p = (n: number) => String(n).padStart(2, "0");
  return `${dt.getUTCFullYear()}-${p(dt.getUTCMonth() + 1)}-${p(dt.getUTCDate())}`;
}

function parseDates(
  v: unknown,
  opts: { tz: string; now: string; allow: Set<string> },
): Result<string[]> {
  if (!Array.isArray(v)) return fail("dates must be an array");
  if (v.length < 1) return fail("at least one date is required");
  const nowMs = Date.parse(opts.now);
  if (!Number.isFinite(nowMs)) return fail("invalid now");
  const today = zonedParts(Math.floor(nowMs / 60_000), opts.tz).date;
  const yesterday = addDays(today, -1);
  const max = addDays(today, 730);
  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of v) {
    if (typeof item !== "string" || !DATE_RE.test(item)) return fail("dates must be YYYY-MM-DD");
    if (!isRealDate(item)) return fail("invalid calendar date");
    // Yesterday is allowed deliberately: "today" depends on the zone, and an organizer whose clock
    // has already rolled over should still be able to include the day their group is picking.
    // Anything before that really is past, which is what the message says.
    if (item < yesterday && !opts.allow.has(item)) return fail("date is in the past");
    if (item > max) return fail("date is too far in the future");
    if (seen.has(item)) continue;
    seen.add(item);
    out.push(item);
  }
  out.sort();
  if (out.length > DATES_MAX) return fail("at most 31 dates");
  return { ok: true, value: out };
}

function isRealDate(iso: string): boolean {
  const bits = iso.split("-");
  const y = Number(bits[0]);
  const m = Number(bits[1]);
  const d = Number(bits[2]);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

function parsePolls(v: unknown): Result<PollDef[]> {
  if (v === undefined || v === null) return { ok: true, value: [] };
  if (!Array.isArray(v)) return fail("polls must be an array");
  if (v.length > POLLS_MAX) return fail("at most 5 polls");
  const polls: PollDef[] = [];
  const pollIds = new Set<string>();
  for (const item of v) {
    if (!isRecord(item)) return fail("invalid poll");
    const id = trimStr(item.id);
    if (!id.ok || !ID_RE.test(id.value) || id.value === "__proto__" || id.value === "constructor" || id.value === "prototype") {
      return fail("invalid poll id");
    }
    if (pollIds.has(id.value)) return fail("duplicate poll id");
    pollIds.add(id.value);
    const question = trimStr(item.question);
    if (
      !question.ok ||
      !hasVisible(question.value) ||
      question.value.length < 1 ||
      question.value.length > QUESTION_MAX
    ) {
      return fail("poll question must be 1 to 80 characters");
    }
    if (!Array.isArray(item.options)) return fail("poll options required");
    if (item.options.length < OPTIONS_MIN || item.options.length > OPTIONS_MAX) {
      return fail("each poll needs 2 to 12 options");
    }
    const options: { id: string; label: string }[] = [];
    const optIds = new Set<string>();
    for (const opt of item.options) {
      if (!isRecord(opt)) return fail("invalid poll option");
      const oid = trimStr(opt.id);
      if (!oid.ok || !ID_RE.test(oid.value) || oid.value === "__proto__" || oid.value === "constructor" || oid.value === "prototype") {
        return fail("invalid option id");
      }
      if (optIds.has(oid.value)) return fail("duplicate option id");
      optIds.add(oid.value);
      const label = trimStr(opt.label);
      if (!label.ok || !hasVisible(label.value) || label.value.length < 1 || label.value.length > LABEL_MAX) {
        return fail("option label must be 1 to 40 characters");
      }
      options.push({ id: oid.value, label: label.value });
    }
    polls.push({ id: id.value, question: question.value, options });
  }
  return { ok: true, value: polls };
}

function parseDeadline(v: unknown): Result<string | null> {
  if (v === undefined || v === null || v === "") return { ok: true, value: null };
  if (typeof v !== "string") return fail("deadline must be an ISO instant");
  const t = Date.parse(v);
  if (!Number.isFinite(t)) return fail("deadline must be an ISO instant");
  return { ok: true, value: new Date(t).toISOString() };
}

function parseAvailability(
  v: unknown,
  ev: EventDef,
): { ok: true; value: Record<string, Level>; ignored: number } | { ok: false; error: string } {
  if (v === undefined || v === null) return { ok: true, value: {}, ignored: 0 };
  if (!isRecord(v)) return fail("availability must be an object");
  const entries = Object.entries(v);
  if (entries.length > SLOTS_MAX) return fail("too many availability keys");
  const valid = new Set(slotStarts(ev).map(String));
  const out: Record<string, Level> = {};
  let ignored = 0;
  for (const [k, val] of entries) {
    if (!SLOT_KEY_RE.test(k)) return fail("availability key is invalid");
    if (val !== 0 && val !== 1 && val !== 2 && val !== 3) {
      return fail("availability level must be 0, 1, 2, or 3");
    }
    if (!valid.has(k)) {
      ignored += 1;
      continue;
    }
    if (val !== 0) out[k] = val;
  }
  return { ok: true, value: out, ignored };
}

function parsePollVotes(
  v: unknown,
  ev: EventDef,
): { ok: true; value: Record<string, Record<string, PollVote>>; ignored: number } | { ok: false; error: string } {
  if (v === undefined || v === null) return { ok: true, value: {}, ignored: 0 };
  if (!isRecord(v)) return fail("pollVotes must be an object");
  const pollMap = new Map(ev.polls.map((p) => [p.id, new Set(p.options.map((o) => o.id))]));
  const out: Record<string, Record<string, PollVote>> = {};
  let ignored = 0;
  for (const [pollId, votes] of Object.entries(v)) {
    const opts = pollMap.get(pollId);
    if (!opts) {
      ignored += 1;
      continue;
    }
    if (!isRecord(votes)) return fail("poll votes must be an object");
    const kept: Record<string, PollVote> = {};
    for (const [optId, vote] of Object.entries(votes)) {
      if (vote !== 0 && vote !== 1 && vote !== 2) return fail("poll vote must be 0, 1, or 2");
      if (!opts.has(optId)) {
        ignored += 1;
        continue;
      }
      kept[optId] = vote;
    }
    out[pollId] = kept;
  }
  return { ok: true, value: out, ignored };
}

function isValidTimeZone(tz: string): boolean {
  try {
    const resolved = new Intl.DateTimeFormat("en-US", { timeZone: tz }).resolvedOptions().timeZone;
    return resolved === tz;
  } catch {
    return false;
  }
}
